import { MouseEvent, useEffect, useRef, useState } from 'react'
import { Box, Button, Card, Stack, Typography } from "@mui/material"
import { TypCharacteristics, TypDescription, TypTitle } from "./style"
import { useNavigate } from "react-router-dom"
import { StyledBadge } from "../../../ui-kit"
import { routesProfile } from "../../../../consts"
import modalEmitter from "../../../ModalComponent/modalEmitter"
import { InviteProjectModalBody, InviteTeamModalBody } from "../../../Modals"
import type { TUserListItem } from '../../../../api/types/users'
import { profileApi } from '../../../../api/profile'
import { USER_STATUS_OPTIONS } from '../../../../consts/userStatus'
import { UserAvatar } from '../../../common/UserAvatar'
import { useFollowUserMutation, useUnfollowUserMutation } from '../../../../api/users'
import { toast } from 'react-toastify'
import { useSettledToggleNetwork } from '../../../../hooks/useSettledToggleNetwork'

interface MobileCardUserProps {
    user: TUserListItem
}

export const MobileCard = ({ user }: MobileCardUserProps) => {
    const navigate = useNavigate();
    const { data: me } = profileApi.useGetProfileQuery()
    const [followUser, { isLoading: isFollowLoading }] = useFollowUserMutation()
    const [unfollowUser, { isLoading: isUnfollowLoading }] = useUnfollowUserMutation()
    const [isFollowed, setIsFollowed] = useState(user.is_followed === true)
    const followSettle = useSettledToggleNetwork()
    const followBurstRef = useRef<boolean | null>(null)

    useEffect(() => {
        setIsFollowed(user.is_followed === true)
    }, [user.id, user.is_followed])

    const locationPart = user.location !== null && user.location !== '' ? `${user.location}, ` : ''
    const competencies = user.competencies.map((item) => item.name)
    const participation = user.participation.map((item) => item.name)
    const statusOption = USER_STATUS_OPTIONS.find((option) => String(option.value) === user.status)
    const statusLabel = statusOption?.label ?? user.status
    const navigateToUser = () => {
        navigate(`/${routesProfile.USERS.path}/${user.slug}`)
    }

    const isFollowActionLoading = isFollowLoading || isUnfollowLoading
    const followActionLabel = isFollowActionLoading
        ? 'Загрузка...'
        : (isFollowed ? 'Отписаться' : 'Подписаться')

    const handleFollowClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (isFollowActionLoading) {
            return
        }

        if (followBurstRef.current === null) {
            followBurstRef.current = isFollowed
        }
        const next = !isFollowed
        setIsFollowed(next)

        followSettle.schedule({
            desired: next,
            commit: async (desired) => {
                followBurstRef.current = null
                if (desired) {
                    await followUser({ userId: user.id }).unwrap()
                    toast.success('Вы подписались на пользователя')
                } else {
                    await unfollowUser({ userId: user.id }).unwrap()
                    toast.success('Вы отписались от пользователя')
                }
            },
            onSkip: () => {
                followBurstRef.current = null
            },
            onError: (baseline) => {
                followBurstRef.current = null
                setIsFollowed(baseline)
                toast.error('Не удалось изменить подписку')
            },
        })
    };
    const handleEdit = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        navigate(`/${routesProfile.PROFILE.path}`)
    };

    const handleInviteProjectClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        modalEmitter.emitOpenModal({
            dialog: {
                title: "Пригласить в проект",
                body: <InviteProjectModalBody userId={user.id} />,
            },
            closeAtion: { visible: false },
            confirmAtion: { visible: false },
        })
    }
    
    const handleInviteTeamClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        modalEmitter.emitOpenModal({
            dialog: {
                title: "Пригласить в команду",
                body: <InviteTeamModalBody userId={user.id} />,
            },
            closeAtion: { visible: false },
            confirmAtion: { visible: false },
        })
    }

    return(
        <Card 
            variant="primaryAdaptiveCard" 
            onClick={navigateToUser}
            sx={{ overflow: 'visible', }}
        >
            <Stack 
                direction="column" 
                p="12px 0px 16px 0px"
                width="100%"
            >
                <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" flex={1} minWidth={0}>
                        <Typography sx={TypTitle} noWrap mr={2} variant="black-14-medium">
                            {user.shortname}
                        </Typography>
                        <StyledBadge invisible={!user.is_online} variant="dot" />
                    </Stack>
                    {me?.id === user.id ? (
                        <Button variant="follow" sx={{ ml: 2 }} onClick={handleEdit}>Редактировать</Button>
                    ) : (
                        <Button
                            variant="follow"
                            sx={{ ml: 2 }}
                            onClick={handleFollowClick}
                            disabled={isFollowActionLoading}
                        >
                            {followActionLabel}
                        </Button>
                    )}
                </Stack>
                <Typography variant='h4' sx={TypDescription}>{locationPart}{user.age} лет</Typography>
                <Typography variant='h4' sx={TypDescription}>{statusLabel}</Typography>
            </Stack>
            <Stack flexDirection="row" gap={2} alignItems="center" pb={2}>
                <UserAvatar src={user.avatar} size={90} />
                <Stack gap={1}>
                    <Typography 
                        variant='body1' 
                        sx={TypCharacteristics}
                    >
                        <Box component='span'>Компетенции: </Box>{competencies.join("; ")}
                    </Typography>
                    <Typography 
                        variant='body1' 
                        sx={TypCharacteristics}
                    >
                        <Box component='span'>Условия участия: </Box>{participation.join("; ")}
                    </Typography>         
                </Stack>
            </Stack>
            {me?.id !== user.id && (
                <Stack 
                    direction="row" 
                    gap={1.5} 
                    padding="0px 12px 12px 0px"
                >
                    <Button variant="action" onClick={handleInviteProjectClick}>Пригласить в проект</Button>
                    <Button variant="action" onClick={handleInviteTeamClick}>Пригласить в команду</Button>
                </Stack>
            )}
        </Card>
    )
}
